<template>
  <v-container>
    <v-card>
      <div
        v-if="firstLoad"
        class="forms-queue-skeleton pa-6"
      >
        <div class="forms-queue-skeleton__header">
          <div
            v-for="column in 6"
            :key="`header-${column}`"
            class="forms-queue-skeleton__cell"
          >
            <v-skeleton-loader type="text"></v-skeleton-loader>
          </div>
        </div>
        <div
          v-for="row in 10"
          :key="`row-${row}`"
          class="forms-queue-skeleton__row"
        >
          <div class="forms-queue-skeleton__cell forms-queue-skeleton__cell--state">
            <div class="forms-queue-skeleton__state-pill">
              <span
                v-for="segment in 4"
                :key="`segment-${row}-${segment}`"
                class="forms-queue-skeleton__state-segment"
              ></span>
              <span class="forms-queue-skeleton__state-label"></span>
            </div>
          </div>
          <div class="forms-queue-skeleton__cell">
            <v-skeleton-loader type="text@2"></v-skeleton-loader>
          </div>
          <div class="forms-queue-skeleton__cell">
            <v-skeleton-loader type="text"></v-skeleton-loader>
          </div>
          <div class="forms-queue-skeleton__cell">
            <v-skeleton-loader type="text"></v-skeleton-loader>
          </div>
          <div class="forms-queue-skeleton__cell">
            <v-skeleton-loader type="text"></v-skeleton-loader>
          </div>
          <div class="forms-queue-skeleton__cell forms-queue-skeleton__cell--actions">
            <span class="forms-queue-skeleton__icon"></span>
            <span class="forms-queue-skeleton__icon"></span>
          </div>
        </div>
        <div class="forms-queue-skeleton__footer">
          <v-skeleton-loader type="text"></v-skeleton-loader>
          <v-skeleton-loader type="text"></v-skeleton-loader>
          <div class="forms-queue-skeleton__footer-actions">
            <span class="forms-queue-skeleton__icon"></span>
            <span class="forms-queue-skeleton__icon"></span>
          </div>
        </div>
      </div>

      <v-data-table
        :headers="pending_headers"
        :items="pending_forms"
        :items-per-page="30"
        :footer-props="{
          itemsPerPageOptions: [15, 30, -1],
          itemsPerPageText: $t('table.itemsPerPageText')
        }"
        sort-by="created_at"
        class="table-cursor"
        v-bind:sort-desc="true"
        :loading="loading"
        :loading-text="$t('table.loading')"
        v-show="!firstLoad"
      >
        <template v-slot:item.state="{ item }">
          <div class="d-flex justify-center">
            <ModeratedOperationState
              :state="item.state"
              :finished="item.finished"
              :authorized="item.authorized"
              :form-name="item.form_name"
              display="stepper-text"
              text-variant="state"
            />
          </div>
        </template>
        <template v-slot:item.created_at="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">{{
                timeDifference(new Date(item.created_at))
              }}</span>
            </template>
            <span
              >{{ new Date(item.created_at).toISOString().substr(0, 10) }}
              {{ new Date(item.created_at).toTimeString().substr(0, 8) }}</span
            >
          </v-tooltip>
        </template>
        <template v-slot:item.form_name="{ item }">
          <div
            class="text-truncate"
            style="max-width:380px;"
          >{{ getFormName(item.form_name) }}<br><span class="text-caption">{{ item.title }}</span></div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              :to="(item.state === 'PV' || item.state === 'PA' || item.state === 'RJ') ? `/manage/forms/${item.object_type}/${item.form_name}/${item.id}` : `/manage/forms/process/${item.object_type}/${item.form_name}/${item.id}`"
              icon
              small
              class="mr-2"
              color="primary"
            >
              <v-icon>mdi-cog</v-icon>
            </v-btn>
            <v-menu
              v-if="item.state === 'VD' || item.state === 'PV' || item.state === 'PC'"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="actionLoadingId === item.id"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-if="item.state === 'VD' || item.state === 'PV'"
                  @click="returnToPending(item)"
                  :disabled="actionLoadingId === item.id"
                >
                  <v-list-item-icon>
                    <v-icon small>mdi-backup-restore</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      getReturnPendingButtonLabel(item)
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-tooltip
                  v-if="item.state === 'PC' && !item.finished"
                  bottom
                  :disabled="canUndoProcess(item)"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-list-item
                        @click="confirmUndoProcess(item)"
                        :disabled="actionLoadingId === item.id || !canUndoProcess(item)"
                      >
                        <v-list-item-icon>
                          <v-icon
                            small
                            :class="{ 'grey--text text--darken-1': !canUndoProcess(item) }"
                          >
                            mdi-undo
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            :class="{ 'grey--text text--darken-1': !canUndoProcess(item) }"
                            :style="!canUndoProcess(item) ? 'text-decoration: line-through;' : ''"
                          >
                            {{ $t("button.undoProcess") }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </div>
                  </template>
                  <span>{{ $t("forms.undoProcessForbidden") }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </div>
        </template>
        <template v-slot:item.owner="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">{{ item.owner.first_name }}</span>
            </template>
            <span>{{ item.owner.first_name }} {{ item.owner.last_name }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.supervisor="{ item }">
          <v-tooltip 
            top
            v-if = "item.supervisor && item.supervisor.first_name && item.supervisor.last_name"
          >
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                class="forms-queue__supervisor"
              >
                <span
                  v-if="isCurrentUserSupervisor(item)"
                  class="forms-queue__supervisor-dot"
                ></span>
                <span>{{ item.supervisor.first_name }}</span>
              </span>
            </template>
            <span>{{ item.supervisor.first_name }} {{ item.supervisor.last_name }}</span>
          </v-tooltip>
          <span v-else>-</span>
        </template>
      </v-data-table>
      <v-dialog
        v-model="undoProcessDialog"
        max-width="520"
      >
        <v-card>
          <v-card-title class="headline">
            {{ $t("forms.undoProcessConfirmTitle") }}
          </v-card-title>
          <v-card-text>
            {{
              $t("forms.undoProcessConfirmText", [
                undoProcessFormTitle
              ])
            }}
            <v-alert
              v-if="undoProcessRelatedForms.length"
              type="error"
              prominent
              class="mt-4"
            >
              <strong>{{ $t("forms.undoProcessRelatedWarningTitle") }}</strong>
              <div class="mt-2">
                {{ $t("forms.undoProcessRelatedWarningText") }}
              </div>
              <ul class="mt-3 pl-4">
                <li
                  v-for="form in undoProcessRelatedForms"
                  :key="form.id"
                >
                  <span class="font-weight-medium">{{ getFormName(form.form_name) }}</span>
                  <template v-if="form.title"> — {{ form.title }}</template>
                </li>
              </ul>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeUndoProcessDialog"
              :disabled="undoProcessSubmitting"
            >
              {{ $t("button.cancel") }}
            </v-btn>
            <v-btn
              color="error"
              @click="undoProcess"
              :loading="undoProcessSubmitting"
              :disabled="undoProcessSubmitting"
            >
              <v-icon left>mdi-undo</v-icon>
              {{ $t("button.undoProcess") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <v-row
      v-show="!firstLoad"
      class="mt-4"
    >
      <v-col class="text-right">
        <v-btn
          color="secondary"
          class="elevated"
          to="/manage/forms/history"
        >
          <v-icon left>mdi-history</v-icon>
          <span>{{ $t('table.finishedFormsAll') }}</span>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import investigatorTablesMethods from "../../mixins/investigatorTablesMethods";
import ModeratedOperationState from "../reusables/ModeratedOperationState";

const ADDITIONAL_MODIFICATION_FORMS = [
  "affiliationform",
  "personalform",
  "vinculationform",
  "workplaceassignationform",
  "modifyresponsibleform",
  "modifyservicesform",
  "modifyexpirationdateform",
  "modifyvinculationform",
  "modifyworkplaceform"
];
export default {
  mixins: [investigatorTablesMethods],
  components: { ModeratedOperationState },
  data() {
    return {
      loading: true,
      firstLoad: true,
      pending_forms: [],
      actionLoadingId: null,
      undoProcessDialog: false,
      undoProcessItem: null,
      undoProcessSubmitting: false,
      undoProcessRelatedForms: []
    };
  },
  computed: {
    pending_headers() {
      return [
        {
          text: this.$t("state.state"),
          value: "state",
          width: "15%",
          align: "center"
        },
        {
          text: this.$t("formName"),
          value: "form_name",
          width: "40%"
        },
        { text: this.$t("owner"), value: "owner"},
        {
          text: this.$t("supervisor"),
          value: "supervisor",
        },
        { text: this.$t("create"), value: "created_at" },
        {
          text: this.$t("actions"),
          value: "actions",
          width: "10%",
          align: "end",
          filterable: false,
          sortable: false
        }
      ];
    },
    undoProcessFormTitle() {
      if (!this.undoProcessItem) {
        return "";
      }
      if (this.undoProcessItem.title) {
        return this.undoProcessItem.title;
      }
      return this.$t(`forms.titles.${this.undoProcessItem.form_name}`);
    }
  },
  created() {
    this.initialize();
    this.getAuthorizeFormsList();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/moderatedOperations/review";
      axios
        .get(path)
        .then(response => {
          this.pending_forms = response.data.filter(form => form.finished !== true && form.state !== "RJ");
          this.loading = false;
          this.firstLoad = false;
        })
        .catch(error => {
          console.log(error);
          this.loading = false;
          this.firstLoad = false;
        });
    },
    returnToPending(item) {
      const formTranslation = this.$t(`forms.titles.${item.form_name}`);
      this.actionLoadingId = item.id;
      const path =
        this.$store.state.endpoint +
        "/moderatedOperations/review/" +
        item.object_type +
        "/returnPending/" +
        item.id;
      axios
        .patch(path, {})
        .then(response => {
          this.$store.commit("alert", {
            color: "success",
            text: this.getReturnPendingSuccessMessage(item, formTranslation)
          });
          this.updateFormInList(response.data);
        })
        .catch(error => {
          console.log(error);
          this.$store.commit("alert", {
            color: "error",
            text: this.getReturnPendingErrorMessage(item)
          });
        })
        .finally(() => {
          this.actionLoadingId = null;
        });
    },
    confirmUndoProcess(item) {
      if (!this.canUndoProcess(item)) {
        return;
      }
      this.undoProcessItem = item;
      this.undoProcessRelatedForms = this.findRelatedModificationForms(item);
      this.undoProcessDialog = true;
    },
    closeUndoProcessDialog() {
      if (this.undoProcessSubmitting) {
        return;
      }
      this.undoProcessDialog = false;
      this.undoProcessItem = null;
      this.undoProcessRelatedForms = [];
    },
    undoProcess() {
      if (!this.undoProcessItem) {
        return;
      }
      const item = this.undoProcessItem;
      const formTranslation = this.$t(`forms.titles.${item.form_name}`);
      this.actionLoadingId = item.id;
      this.undoProcessSubmitting = true;
      const path =
        this.$store.state.endpoint +
        "/moderatedOperations/review/" +
        item.object_type +
        "/undoProcess/" +
        item.id;
      axios
        .patch(path, {})
        .then(response => {
          this.$store.commit("alert", {
            color: "success",
            text: this.$t("forms.undoProcess", [formTranslation])
          });
          this.updateFormInList(response.data);
          this.undoProcessDialog = false;
          this.undoProcessItem = null;
          this.undoProcessRelatedForms = [];
        })
        .catch(error => {
          console.log(error);
          this.$store.commit("alert", {
            color: "error",
            text: this.$t("forms.undoProcessError")
          });
        })
        .finally(() => {
          this.actionLoadingId = null;
          this.undoProcessSubmitting = false;
        });
    },
    updateFormInList(updatedItem) {
      const pendingIndex = this.pending_forms.findIndex(
        form => form.id === updatedItem.id
      );
      if (updatedItem.finished === true || updatedItem.state === "RJ") {
        if (pendingIndex !== -1) {
          this.pending_forms.splice(pendingIndex, 1);
        }
        return;
      } else if (pendingIndex !== -1) {
        this.$set(this.pending_forms, pendingIndex, updatedItem);
      } else {
        this.pending_forms.unshift(updatedItem);
      }
    },
    getReturnPendingButtonLabel(item) {
      const currentState = this.getNameState(item.state, item.finished).toLowerCase();
      return this.$t("button.undoState", [currentState]);
    },
    getReturnPendingSuccessMessage(item, formTranslation) {
      if (item.state === "PV") {
        return this.$t("forms.returnPendingAssignment", [formTranslation]);
      }
      return this.$t("forms.returnPending", [formTranslation]);
    },
    getReturnPendingErrorMessage(item) {
      if (item.state === "PV") {
        return this.$t("forms.returnPendingAssignmentError");
      }
      return this.$t("forms.returnPendingError");
    },
    isCurrentUserSupervisor(item) {
      if (
        item &&
        item.supervisor &&
        item.supervisor.username &&
        this.getUser &&
        this.getUser.username &&
        item.supervisor.username === this.getUser.username
      ) {
        return true;
      }
      return false;
    },
    canUndoProcess(item) {
      return !this.isModificationForm(item);
    },
    isModificationForm(form) {
      if (!form || !form.form_name) {
        return false;
      }
      const name = form.form_name.toLowerCase();
      return (
        name.startsWith("modify") ||
        ADDITIONAL_MODIFICATION_FORMS.includes(name)
      );
    },
    findRelatedModificationForms(item) {
      if (!item || !item.object_id) {
        return [];
      }
      return this.pending_forms.filter(form => {
        return (
          form.id !== item.id &&
          form.object_id === item.object_id &&
          this.isModificationForm(form)
        );
      });
    }
  }
};
</script>

<style scoped>
.forms-queue-skeleton__header,
.forms-queue-skeleton__row {
  display: grid;
  grid-template-columns: 170px minmax(320px, 1.8fr) 150px 160px 170px 96px;
  column-gap: 16px;
  align-items: center;
}

.forms-queue-skeleton__header {
  margin-bottom: 10px;
}

.forms-queue-skeleton__row {
  min-height: 50px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.forms-queue-skeleton__cell {
  min-width: 0;
}

.forms-queue-skeleton__cell--state {
  display: flex;
  justify-content: center;
}

.forms-queue-skeleton__state-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.025);
}

.forms-queue-skeleton__state-segment,
.forms-queue-skeleton__state-label,
.forms-queue-skeleton__icon {
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.12) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: forms-queue-skeleton-shimmer 1.4s ease-in-out infinite;
}

.forms-queue-skeleton__state-segment {
  width: 11px;
  height: 6px;
  border-radius: 999px;
}

.forms-queue-skeleton__state-label {
  width: 46px;
  height: 8px;
  border-radius: 999px;
}

.forms-queue-skeleton__cell--actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.forms-queue-skeleton__icon {
  width: 18px;
  height: 18px;
  border-radius: 999px;
}

.forms-queue-skeleton__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.forms-queue-skeleton__footer-actions {
  display: flex;
  gap: 8px;
}

@keyframes forms-queue-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.forms-queue__supervisor {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.forms-queue__supervisor-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background-color: var(--v-primary-base);
  box-shadow: 0 0 0 3px rgba(255, 100, 34, 0.16);
}
</style>
